// @flow
import React, { useMemo } from 'react';
import type { Node } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import uniq from 'lodash/uniq';
import isArray from 'lodash/isArray';
import { useFormSubmitterContext } from '../../form-submitter-context';
import Group from '../../../../common/components/group';
import FormElement from '..';
import { EMAIL_ELEMENT } from '../../../../constants';

import type { GroupElement } from '../../../../types/form/Element';

type Props = {
  description?: Node,
  element: GroupElement,
};

const ReactGridLayout = WidthProvider(RGL);

const GroupWithContext = (props: Props) => {
  const { element, description } = props;
  const { id, label, items = [] } = element;
  const {
    state,
    selector: { getComponentForId, getPageLayout },
  } = useFormSubmitterContext();

  const layout = getPageLayout(state);

  const cols = isArray(layout) ? uniq(layout.map(item => item.w)).length : 0;
  // only use grid-layout when there are two column cases in layout
  const useGridLayout = !!layout && cols > 1;

  const components = items
    .map(itemId => getComponentForId(state, itemId))
    .map(component => {
      const { id: componentId } = component;
      const { isAuthenticationRequired } = state;
      if (isAuthenticationRequired && componentId === EMAIL_ELEMENT) {
        return null;
      }
      return useGridLayout ? (
        <div key={componentId}>
          <FormElement key={componentId} element={component} />
        </div>
      ) : (
        <FormElement key={componentId} element={component} />
      );
    });

  const groupComponents = useMemo(() => {
    return useGridLayout ? (
      <ReactGridLayout
        cols={cols}
        data-testid="submitter-grid"
        isDraggable={false}
        isResizable={false}
        layout={layout}
        margin={[0, 0]}
        rowHeight={1}
        useCSSTransforms={false}
      >
        {components}
      </ReactGridLayout>
    ) : (
      components
    );
  }, [cols, components, layout, useGridLayout]);

  return useMemo(() => {
    return (
      <Group
        components={groupComponents}
        description={description}
        id={id}
        inFocus={false}
        label={label}
      />
    );
  }, [groupComponents, description, id, label]);
};

export { GroupWithContext };
export default GroupWithContext;
